/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useRef } from 'react'
import { Link, NavigateFunction, useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import { DealsProposalCurrencyChoices } from '@/apollo/graphql'
import { Button, DatePicker, Form, FormButtons, TextInput } from '@/components'
import { useApp, useForm } from '@/hooks'
import { t } from '@/i18n'
import CarSubModelSelector from '@/pages/Car/carSubModelSelector'
import CustomerSelector from '@/pages/Customer/customerSelector'
import { IProposal } from '@/types'
import { useBuildData } from '../utils'

interface Props {
  isEditing?: boolean
  initialData?: Partial<IProposal>
  loading: boolean
  submitting: boolean
  onCancel: () => void
  onSubmit: (d: IProposal) => void
}

const ProposalForm: React.FC<Props> = ({
  isEditing,
  initialData,
  loading,
  submitting,
  onCancel,
  onSubmit,
}) => {
  const { currency } = useApp()
  const navigate: NavigateFunction = useNavigate()
  const formRef = useRef<HTMLFormElement | null>(null)

  const schema = useMemo(
    () =>
      yup
        .object({
          currency: yup
            .mixed<DealsProposalCurrencyChoices>()
            .oneOf(
              Object.values(DealsProposalCurrencyChoices),
              t('required_field_error'),
            ),
          leaseDuration: yup.number().required(t('required_field_error')),
          // status: yup
          //   .mixed<ProposalStatus>()
          //   .oneOf(Object.values(ProposalStatus), t('required_field_error')),
        })
        .required(),
    [],
  )

  const { control, isValid, getValues, onTriggerChange, onFormSubmit } =
    useForm<any>({
      isEditing,
      defaultValues: initialData,
      schema,
      onSubmit,
    })

  useEffect(() => {
    if (initialData?.customer) {
      onTriggerChange('customer', initialData.customer.id)
    }
  }, [initialData?.customer, onTriggerChange])

  const emptyValue = useMemo(() => (isEditing ? null : '...'), [isEditing])

  const proposalField = useBuildData(initialData)

  return (
    <>
      <div className="main-info">
        <div className="close-box">
          <Button onClick={onCancel}>
            <span className="icon-close"></span>
          </Button>
        </div>
        <div className="section-box">
          <div className="toolbox">
            <div className="title-box">
              <h4>{t('edit')}</h4>
            </div>
            <Link to={'/deals/proposals/' + initialData?.id}>
              {t('full_edit')}
            </Link>
          </div>

          <div className="edit-data">
            <Form<IProposal>
              formRef={formRef}
              control={control}
              isEditing={isEditing}
              onSubmit={onFormSubmit}
              className={isEditing ? 'editing' : 'view'}
              currency={getValues('currency') || currency}
            >
              <div className="form-fields">
                {Object.keys(proposalField || {}).map(key => {
                  // @ts-ignore
                  return proposalField[key].map(field => {
                    if (!field.name || field.editable === false) return null
                    return (
                      <TextInput
                        key={field.name}
                        name={field.name}
                        control={control}
                        label={t(field.label)}
                        emptyValue={emptyValue}
                        className={field.className}
                        disabled={!Boolean(isEditing)}
                      />
                    )
                  })
                })}
                <DatePicker
                  className="w-50"
                  control={control}
                  name="creationTime"
                  emptyValue={emptyValue}
                  label={t('creation_time')}
                  disabled={!Boolean(isEditing)}
                />
                <CustomerSelector
                  isFormElement
                  className="w-50"
                  control={control}
                  isEditing={isEditing}
                  initial={initialData?.customer}
                  selected={getValues('customer')}
                />
                <CarSubModelSelector
                  isFormElement
                  className="w-50"
                  control={control}
                  loading={loading}
                  isEditing={isEditing}
                  initialBrand={initialData?.carSubModel?.carModel?.brand}
                  initialModel={initialData?.carSubModel?.carModel}
                  initialSubModel={initialData?.carSubModel}
                  onTriggerChange={onTriggerChange}
                />
              </div>
            </Form>
          </div>
        </div>
      </div>
      <FormButtons
        formRef={formRef}
        visible={isEditing}
        onCancel={onCancel}
        isFormValid={isValid}
        submitting={submitting}
        cancelClassName={'gray'}
      />
    </>
  )
}

export default ProposalForm
